import { useState, useEffect, useCallback } from 'react';
import { bookingService } from '../services/bookingService';
import { sectionService } from '../services/sectionService';

export const useBookingData = (user) => {
    // Booking states
    const [myBookings, setMyBookings] = useState([]);
    const [pendingBookings, setPendingBookings] = useState([]);
    const [approvedBookings, setApprovedBookings] = useState([]);
    const [rejectedBookings, setRejectedBookings] = useState([]);
    const [cancelledBookings, setCancelledBookings] = useState([]);
    const [waitlistBookings, setWaitlistBookings] = useState([]);

    // Member states
    const [sectionMembers, setSectionMembers] = useState({});
    const [pendingMembers, setPendingMembers] = useState([]);
    const [approvedMembers, setApprovedMembers] = useState([]);
    const [waitlistMembers, setWaitlistMembers] = useState([]);

    // Notification states
    const [notifications, setNotifications] = useState([]);

    // Filter states
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterType, setFilterType] = useState('all');
    const [filterFacility, setFilterFacility] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [dateRange, setDateRange] = useState({ start: '', end: '' });
    const [sortBy, setSortBy] = useState('date_desc');

    // Selection states
    const [selectedBookings, setSelectedBookings] = useState([]);

    // Booking counter
    const [bookingCounter, setBookingCounter] = useState(1000);

    // Delegation states
    const [delegatedUsers, setDelegatedUsers] = useState([]);
    const [presidentDelegation, setPresidentDelegation] = useState(false);

    // Audit states
    const [auditLog, setAuditLog] = useState([]);

    // Communication states
    const [communicationHistory, setCommunicationHistory] = useState([]);

    // Location change states
    const [locationChangeHistory, setLocationChangeHistory] = useState([]);

    // Conflict states
    const [conflicts, setConflicts] = useState([]);

    // Feedback states
    const [feedbacks, setFeedbacks] = useState([]);

    // Maintenance states
    const [maintenanceSchedule, setMaintenanceSchedule] = useState([]);

    // Blackout states
    const [blackoutDates, setBlackoutDates] = useState([]);

    // Pricing states
    const [pricingRules, setPricingRules] = useState([]);

    // Permissions states
    const [permissions, setPermissions] = useState({});

    // Announcement states
    const [announcements, setAnnouncements] = useState([]);

    // Analytics states
    const [showAnalytics, setShowAnalytics] = useState(false);
    const [analyticsPeriod, setAnalyticsPeriod] = useState('month');

    // Loading state
    const [loading, setLoading] = useState(false);

    // Stats
    const [stats, setStats] = useState({
        total: 0,
        pending: 0,
        approved: 0,
        rejected: 0,
        cancelled: 0,
        waitlist: 0,
        today: 0,
        thisWeek: 0,
        thisMonth: 0,
        religious: 0,
        political: 0,
        sporting: 0,
        equipment: 0,
        facilities: 0,
        memberships: 0,
        locationChanged: 0
    });

    // ========== Fetch from API instead of localStorage ==========
    const loadAllData = useCallback(async () => {
        if (!user?.id) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            
            // Fetch bookings from API
            const bookingsResponse = await bookingService.getUserBookings();
            
            if (bookingsResponse.success && bookingsResponse.bookings) {
                const allBookings = bookingsResponse.bookings;
                
                // Separate by status
                const userBookings = allBookings.filter(b => b.member_id === user.id);
                const pending = allBookings.filter(b => b.status === 'pending');
                const approved = allBookings.filter(b => b.status === 'approved');
                const rejected = allBookings.filter(b => b.status === 'rejected');
                const cancelled = allBookings.filter(b => b.status === 'cancelled');
                const waitlist = allBookings.filter(b => b.status === 'waitlist');
                
                setMyBookings(userBookings);
                setPendingBookings(pending);
                setApprovedBookings(approved);
                setRejectedBookings(rejected);
                setCancelledBookings(cancelled);
                setWaitlistBookings(waitlist);
                
                // Update stats
                updateStatsFromBookings(allBookings);
            }
            
            // Fetch section memberships
            const membershipsResponse = await sectionService.getMyMemberships();
            if (membershipsResponse.success && membershipsResponse.memberships) {
                const memberships = membershipsResponse.memberships;
                setPendingMembers(memberships.filter(m => m?.status === 'pending'));
                setApprovedMembers(memberships.filter(m => m?.status === 'approved'));
                setWaitlistMembers(memberships.filter(m => m?.status === 'waitlist'));
            }
            
        } catch (error) {
            console.error('Error loading data from API:', error);
        } finally {
            setLoading(false);
        }
    }, [user]);

    // Load data on mount and when user changes
    useEffect(() => {
        loadAllData();
    }, [loadAllData]);

    // Update statistics
    const updateStatsFromBookings = (allBookings) => {
        const today = new Date().toISOString().split('T')[0];
        const thisWeek = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
        const thisMonth = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();

        setStats({
            total: allBookings.length,
            pending: allBookings.filter(b => b.status === 'pending').length,
            approved: allBookings.filter(b => b.status === 'approved').length,
            rejected: allBookings.filter(b => b.status === 'rejected').length,
            cancelled: allBookings.filter(b => b.status === 'cancelled').length,
            waitlist: allBookings.filter(b => b.status === 'waitlist').length,
            today: allBookings.filter(b => b.booking_date === today).length,
            thisWeek: allBookings.filter(b => b.booking_date >= thisWeek).length,
            thisMonth: allBookings.filter(b => b.booking_date >= thisMonth).length,
            religious: allBookings.filter(b => b.event_type === 'religious').length,
            political: allBookings.filter(b => b.event_type === 'political').length,
            sporting: allBookings.filter(b => b.sportingOnly).length,
            equipment: allBookings.filter(b => b.booking_type === 'equipment').length,
            facilities: allBookings.filter(b => b.booking_type === 'facility').length,
            memberships: allBookings.filter(b => b.booking_type === 'section').length,
            locationChanged: allBookings.filter(b => b.status === 'location_changed').length
        });
    };

    // Generate booking ID
    const generateBookingId = () => {
        const newCounter = bookingCounter + 1;
        setBookingCounter(newCounter);
        localStorage.setItem('nlngra_booking_counter', newCounter.toString());
        return `RA-${newCounter}`;
    };

    // Add notification
    const addNotification = (message, type = 'info', details = {}) => {
        const newNotification = {
            id: Date.now(),
            message,
            type,
            details,
            timestamp: new Date().toISOString(),
            read: false
        };
        const updatedNotifications = [newNotification, ...notifications];
        setNotifications(updatedNotifications);
        localStorage.setItem('nlngra_notifications', JSON.stringify(updatedNotifications));
        return newNotification.id;
    };

    // Mark notification as read
    const markNotificationAsRead = (notificationId) => {
        const updatedNotifications = notifications.map(n =>
            n.id === notificationId ? {...n, read: true } : n
        );
        setNotifications(updatedNotifications);
        localStorage.setItem('nlngra_notifications', JSON.stringify(updatedNotifications));
    };

    // Clear all notifications
    const clearAllNotifications = () => {
        setNotifications([]);
        localStorage.setItem('nlngra_notifications', JSON.stringify([]));
    };

    // Add audit log entry
    const addAuditLog = (action, details = {}) => {
        const newEntry = {
            id: Date.now(),
            user: user?.email,
            userName: (user?.first_name || '') + ' ' + (user?.last_name || ''),
            action,
            details,
            timestamp: new Date().toISOString()
        };
        const updatedLog = [newEntry, ...auditLog].slice(0, 1000);
        setAuditLog(updatedLog);
        localStorage.setItem('nlngra_audit_log', JSON.stringify(updatedLog));
    };

    // Communication functions
    const sendSMS = (phoneNumber, message) => {
        console.log(`Sending SMS to ${phoneNumber}: ${message}`);
        addAuditLog('SMS Sent', { to: phoneNumber, messageLength: message.length });
        addNotification(`SMS sent to ${phoneNumber}`, 'info');
    };

    const sendWhatsApp = (phoneNumber, message) => {
        console.log(`Sending WhatsApp to ${phoneNumber}: ${message}`);
        addAuditLog('WhatsApp Sent', { to: phoneNumber, messageLength: message.length });
        addNotification(`WhatsApp message sent to ${phoneNumber}`, 'info');
    };

    const sendEmail = (email, subject, body) => {
        console.log(`Sending email to ${email}: ${subject} - ${body}`);
        addAuditLog('Email Sent', { to: email, subject });
        addNotification(`Email sent to ${email}`, 'info');
    };

    // Location change function
    const handleLocationChange = async (bookingId, newLocation, reason) => {
        try {
            const response = await bookingService.adminUpdateBooking(bookingId, 'location_changed', reason, newLocation);
            if (response.success) {
                addNotification(`Booking location changed to ${newLocation}`, 'success');
                await loadAllData();
            }
        } catch (error) {
            console.error('Location change error:', error);
            addNotification('Failed to change location', 'error');
        }
    };

    // Waitlist management
    const addToWaitlist = (bookingId) => {
        addAuditLog('Added to Waitlist', { bookingId });
        addNotification('Booking added to waitlist', 'info');
    };

    const removeFromWaitlist = (bookingId) => {
        addAuditLog('Removed from Waitlist', { bookingId });
        addNotification('Booking removed from waitlist', 'info');
    };

    const promoteFromWaitlist = (bookingId) => {
        addAuditLog('Promoted from Waitlist', { bookingId });
        addNotification('Booking promoted from waitlist', 'info');
    };

    // Conflict detection
    const detectConflicts = () => {
        console.log('Detecting conflicts');
    };

    const resolveConflict = (conflictId, resolution) => {
        addAuditLog('Conflict Resolved', { conflictId, resolution });
        addNotification('Conflict resolved', 'success');
    };

    // Feedback management
    const submitFeedback = (bookingId, rating, comment) => {
        const feedback = {
            id: Date.now(),
            bookingId,
            userId: user?.id,
            userName: (user?.first_name || '') + ' ' + (user?.last_name || ''),
            rating,
            comment,
            timestamp: new Date().toISOString(),
            status: 'new',
            response: null
        };

        const updatedFeedbacks = [feedback, ...feedbacks];
        setFeedbacks(updatedFeedbacks);
        localStorage.setItem('nlngra_feedback', JSON.stringify(updatedFeedbacks));

        addAuditLog('Feedback Submitted', { bookingId, rating });
        addNotification('Thank you for your feedback!', 'success');
    };

    const respondToFeedback = (feedbackId, response) => {
        const updatedFeedbacks = feedbacks.map(f => {
            if (f.id === feedbackId) {
                return {...f, response, respondedAt: new Date().toISOString(), respondedBy: user?.email };
            }
            return f;
        });
        setFeedbacks(updatedFeedbacks);
        localStorage.setItem('nlngra_feedback', JSON.stringify(updatedFeedbacks));
        addAuditLog('Feedback Response', { feedbackId });
        addNotification('Feedback response sent', 'info');
    };

    // Maintenance management
    const scheduleMaintenance = (maintenanceData) => {
        const newMaintenance = {
            id: Date.now(),
            ...maintenanceData,
            createdAt: new Date().toISOString(),
            createdBy: user?.email
        };
        const updatedMaintenance = [...maintenanceSchedule, newMaintenance];
        setMaintenanceSchedule(updatedMaintenance);
        localStorage.setItem('nlngra_maintenance', JSON.stringify(updatedMaintenance));
        addAuditLog('Maintenance Scheduled', maintenanceData);
        addNotification(`Maintenance scheduled for ${maintenanceData.facility}`, 'info');
    };

    const cancelMaintenance = (maintenanceId) => {
        const updatedMaintenance = maintenanceSchedule.filter(m => m.id !== maintenanceId);
        setMaintenanceSchedule(updatedMaintenance);
        localStorage.setItem('nlngra_maintenance', JSON.stringify(updatedMaintenance));
        addAuditLog('Maintenance Cancelled', { maintenanceId });
        addNotification('Maintenance cancelled', 'info');
    };

    // Blackout date management
    const addBlackoutDate = (blackoutData) => {
        const newBlackout = {
            id: Date.now(),
            ...blackoutData,
            createdAt: new Date().toISOString(),
            createdBy: user?.email
        };
        const updatedBlackout = [...blackoutDates, newBlackout];
        setBlackoutDates(updatedBlackout);
        localStorage.setItem('nlngra_blackout', JSON.stringify(updatedBlackout));
        addAuditLog('Blackout Date Added', blackoutData);
        addNotification(`Blackout date added for ${blackoutData.facility}`, 'info');
    };

    const removeBlackoutDate = (blackoutId) => {
        const updatedBlackout = blackoutDates.filter(b => b.id !== blackoutId);
        setBlackoutDates(updatedBlackout);
        localStorage.setItem('nlngra_blackout', JSON.stringify(updatedBlackout));
        addAuditLog('Blackout Date Removed', { blackoutId });
        addNotification('Blackout date removed', 'info');
    };

    // Pricing management
    const addPricingRule = (pricingData) => {
        const newPricing = {
            id: Date.now(),
            ...pricingData,
            createdAt: new Date().toISOString(),
            createdBy: user?.email
        };
        const updatedPricing = [...pricingRules, newPricing];
        setPricingRules(updatedPricing);
        localStorage.setItem('nlngra_pricing', JSON.stringify(updatedPricing));
        addAuditLog('Pricing Rule Added', pricingData);
        addNotification('Pricing rule added', 'info');
    };

    const updatePricingRule = (ruleId, updates) => {
        const updatedPricing = pricingRules.map(r => {
            if (r.id === ruleId) {
                return {...r, ...updates };
            }
            return r;
        });
        setPricingRules(updatedPricing);
        localStorage.setItem('nlngra_pricing', JSON.stringify(updatedPricing));
        addAuditLog('Pricing Rule Updated', { ruleId, updates });
        addNotification('Pricing rule updated', 'info');
    };

    const deletePricingRule = (ruleId) => {
        const updatedPricing = pricingRules.filter(r => r.id !== ruleId);
        setPricingRules(updatedPricing);
        localStorage.setItem('nlngra_pricing', JSON.stringify(updatedPricing));
        addAuditLog('Pricing Rule Deleted', { ruleId });
        addNotification('Pricing rule deleted', 'info');
    };

    // Permissions management
    const updateRolePermissions = (roleId, newPermissions) => {
        const updatedPermissions = {...permissions, [roleId]: newPermissions };
        setPermissions(updatedPermissions);
        localStorage.setItem('nlngra_permissions', JSON.stringify(updatedPermissions));
        addAuditLog('Permissions Updated', { roleId, newPermissions });
        addNotification('Permissions updated', 'info');
    };

    // Announcement management
    const createAnnouncement = (announcementData) => {
        const newAnnouncement = {
            id: Date.now(),
            ...announcementData,
            createdAt: new Date().toISOString(),
            createdBy: user?.email
        };
        const updatedAnnouncements = [...announcements, newAnnouncement];
        setAnnouncements(updatedAnnouncements);
        localStorage.setItem('nlngra_announcements', JSON.stringify(updatedAnnouncements));
        addAuditLog('Announcement Created', announcementData);
        addNotification('Announcement posted', 'success');
    };

    const deleteAnnouncement = (announcementId) => {
        const updatedAnnouncements = announcements.filter(a => a.id !== announcementId);
        setAnnouncements(updatedAnnouncements);
        localStorage.setItem('nlngra_announcements', JSON.stringify(updatedAnnouncements));
        addAuditLog('Announcement Deleted', { announcementId });
        addNotification('Announcement deleted', 'info');
    };

    // Selection helpers
    const toggleSelectBooking = (bookingId) => {
        if (selectedBookings.includes(bookingId)) {
            setSelectedBookings(selectedBookings.filter(id => id !== bookingId));
        } else {
            setSelectedBookings([...selectedBookings, bookingId]);
        }
    };

    const toggleSelectAll = (bookings) => {
        if (selectedBookings.length === bookings.length) {
            setSelectedBookings([]);
        } else {
            setSelectedBookings(bookings.map(b => b.id));
        }
    };

    // Get analytics data
    const getAnalytics = () => {
        const allBookings = myBookings;
        const now = new Date();
        let startDate;

        switch (analyticsPeriod) {
            case 'week':
                startDate = new Date(now.setDate(now.getDate() - 7));
                break;
            case 'month':
                startDate = new Date(now.setMonth(now.getMonth() - 1));
                break;
            case 'quarter':
                startDate = new Date(now.setMonth(now.getMonth() - 3));
                break;
            case 'year':
                startDate = new Date(now.setFullYear(now.getFullYear() - 1));
                break;
            default:
                startDate = new Date(now.setMonth(now.getMonth() - 1));
        }

        const filtered = allBookings.filter(b => new Date(b.created_at) >= startDate);

        const byDay = {};
        filtered.forEach(b => {
            const day = b.booking_date;
            byDay[day] = (byDay[day] || 0) + 1;
        });

        const byType = {
            facility: filtered.filter(b => b.booking_type === 'facility').length,
            equipment: filtered.filter(b => b.booking_type === 'equipment').length,
            membership: filtered.filter(b => b.booking_type === 'section').length
        };

        const byStatus = {
            pending: filtered.filter(b => b.status === 'pending').length,
            approved: filtered.filter(b => b.status === 'approved').length,
            rejected: filtered.filter(b => b.status === 'rejected').length,
            cancelled: filtered.filter(b => b.status === 'cancelled').length,
            waitlist: filtered.filter(b => b.status === 'waitlist').length,
            locationChanged: filtered.filter(b => b.status === 'location_changed').length
        };

        const facilityCount = {};
        filtered.filter(b => b.booking_type === 'facility').forEach(b => {
            facilityCount[b.facility_name] = (facilityCount[b.facility_name] || 0) + 1;
        });

        const popularFacilities = Object.entries(facilityCount)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 5);

        const communicationsByType = {
            sms: communicationHistory.filter(c => c.type === 'sms').length,
            whatsapp: communicationHistory.filter(c => c.type === 'whatsapp').length,
            email: communicationHistory.filter(c => c.type === 'email').length
        };

        return {
            total: filtered.length,
            byDay,
            byType,
            byStatus,
            popularFacilities,
            communications: communicationsByType
        };
    };

    return {
        // Bookings
        myBookings,
        setMyBookings,
        pendingBookings,
        setPendingBookings,
        approvedBookings,
        setApprovedBookings,
        rejectedBookings,
        setRejectedBookings,
        cancelledBookings,
        setCancelledBookings,
        waitlistBookings,
        setWaitlistBookings,

        // Members
        sectionMembers,
        setSectionMembers,
        pendingMembers,
        setPendingMembers,
        approvedMembers,
        setApprovedMembers,
        waitlistMembers,
        setWaitlistMembers,

        // Notifications
        notifications,
        setNotifications,
        addNotification,
        markNotificationAsRead,
        clearAllNotifications,

        // Filters
        filterStatus,
        setFilterStatus,
        filterType,
        setFilterType,
        filterFacility,
        setFilterFacility,
        searchTerm,
        setSearchTerm,
        dateRange,
        setDateRange,
        sortBy,
        setSortBy,

        // Selections
        selectedBookings,
        setSelectedBookings,
        toggleSelectBooking,
        toggleSelectAll,

        // Counter
        bookingCounter,
        generateBookingId,

        // Delegation
        delegatedUsers,
        setDelegatedUsers,
        presidentDelegation,
        setPresidentDelegation,

        // Audit
        auditLog,
        setAuditLog,
        addAuditLog,

        // Communications
        communicationHistory,
        setCommunicationHistory,
        sendSMS,
        sendWhatsApp,
        sendEmail,

        // Location changes
        locationChangeHistory,
        setLocationChangeHistory,
        handleLocationChange,

        // Conflicts
        conflicts,
        setConflicts,
        detectConflicts,
        resolveConflict,

        // Feedback
        feedbacks,
        setFeedbacks,
        submitFeedback,
        respondToFeedback,

        // Maintenance
        maintenanceSchedule,
        setMaintenanceSchedule,
        scheduleMaintenance,
        cancelMaintenance,

        // Blackout
        blackoutDates,
        setBlackoutDates,
        addBlackoutDate,
        removeBlackoutDate,

        // Pricing
        pricingRules,
        setPricingRules,
        addPricingRule,
        updatePricingRule,
        deletePricingRule,

        // Permissions
        permissions,
        setPermissions,
        updateRolePermissions,

        // Announcements
        announcements,
        setAnnouncements,
        createAnnouncement,
        deleteAnnouncement,

        // Waitlist management
        addToWaitlist,
        removeFromWaitlist,
        promoteFromWaitlist,

        // Stats and Analytics
        stats,
        setStats,
        getAnalytics,
        showAnalytics,
        setShowAnalytics,
        analyticsPeriod,
        setAnalyticsPeriod,

        // Load function
        loadAllData,
        loading
    };
};